/**
 * What you hear when a shell is held to the ear: not the sea, but the room,
 * resonating in a cavity whose pitch rises as the whorl tightens.
 *
 * Pink noise through a swept resonant bandpass, with a short breath envelope.
 * One-shot; the nodes free themselves when the tail ends.
 */
export class ShellVoice {
  /** @param {import('../AudioEngine.js').AudioEngine} engine */
  constructor(engine) {
    this.engine = engine;
    this.ac = engine.ac;
  }

  play({ pan = 0, bright = 0.5, gain = 0.6 } = {}) {
    const ac = this.ac;
    const now = ac.currentTime;
    const dur = 1.5 + bright * 1.6;

    const src = ac.createBufferSource();
    src.buffer = this.engine.noiseBuffer;
    src.loop = true;
    src.playbackRate.value = 0.7 + bright * 0.8;

    const band = ac.createBiquadFilter();
    band.type = 'bandpass';
    const f0 = 420 + bright * 1500;
    band.frequency.setValueAtTime(f0 * 0.55, now);
    band.frequency.exponentialRampToValueAtTime(f0 * 1.45, now + dur * 0.55);
    band.frequency.exponentialRampToValueAtTime(f0 * 0.8, now + dur);
    band.Q.setValueAtTime(5.5 + bright * 7, now);

    // A second, narrower formant an irrational distance above: the cavity has
    // more than one mode and they do not agree.
    const band2 = ac.createBiquadFilter();
    band2.type = 'bandpass';
    band2.frequency.setValueAtTime(f0 * 2.37, now);
    band2.frequency.exponentialRampToValueAtTime(f0 * 3.1, now + dur);
    band2.Q.value = 11;
    const band2Gain = ac.createGain();
    band2Gain.gain.value = 0.45;

    const env = ac.createGain();
    env.gain.setValueAtTime(0.0001, now);
    env.gain.exponentialRampToValueAtTime(gain * 0.34, now + 0.14);
    env.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    const panner = ac.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, pan));

    src.connect(band);
    band.connect(env);
    src.connect(band2);
    band2.connect(band2Gain);
    band2Gain.connect(env);
    env.connect(panner);
    panner.connect(this.engine.dry);
    panner.connect(this.engine.send);

    src.start(now);
    src.stop(now + dur + 0.1);
    src.onended = () => {
      panner.disconnect();
      env.disconnect();
      band.disconnect();
      band2.disconnect();
      band2Gain.disconnect();
    };
  }
}
